import { getApiFieldName, getDbFieldName } from './dynamicColumns';
import { canonicalFieldKeys, fieldnameKeys, type FieldAliasMap } from './fieldAliases';

interface TemplateDestination {
  columnIds: string[];
  additionalFields: string[];
  availableFields?: ReadonlyArray<{ fieldname: string }>;
  canAddFields: boolean;
  /** What THIS grid's column ids mean in registry terms. Omit it and every column
   *  is matched on its id alone, which is what the registry-driven grids want —
   *  their ids already ARE the fieldname (or its API remap). */
  fieldAliases?: FieldAliasMap;
}

interface TemplateSnapshot extends Record<string, unknown> {
  fields?: string[];
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  additionalFields?: string[];
  grouping?: string[];
  pinnedColumns?: string[];
  boldColumns?: string[];
  columnSizing?: Record<string, number>;
  filterTypes?: Record<string, unknown>;
  sorting?: { id: string }[];
  columnFilters?: { id: string }[];
  aggCalcs?: Record<string, unknown>;
  /** What the SOURCE grid's column ids meant, stamped at save time by
   *  buildTemplateSnapshot. Absent on every snapshot saved before this existed —
   *  those fall back to deriving an identity from the id itself. */
  fieldAliases?: Record<string, readonly string[]>;
}

/** The id-keyed shapes inside a snapshot, so a column renamed between grids is
 *  renamed in ALL of them. Missing any one leaves a layout half-translated: a
 *  sort or a pin still naming the source id is silently dropped on apply. */
const ID_ARRAYS = ['fields', 'columnOrder', 'pinnedColumns', 'boldColumns', 'grouping'] as const;
const ID_RECORDS = ['columnVisibility', 'columnSizing', 'filterTypes', 'aggCalcs'] as const;
const ID_OBJECT_ARRAYS = ['sorting', 'columnFilters'] as const;

/** Resolve against the destination's picker, never the source's column registry.
 * Hidden stale columns are harmless, but sort/filter/group dependencies must exist.
 * Older snapshots lack fields, so infer their visible set from order/visibility.
 *
 * A column the destination spells DIFFERENTLY is resolved through the registry
 * rather than refused: both sides reduce their ids to `field_definitions`
 * fieldnames (declared by the host where the id is not derivable — see
 * FieldAliasMap) and match on those. Resolving alone is not enough, though, since
 * the snapshot is applied BY ID: the whole layout is then rewritten into the
 * destination's ids, or `columnVisibility` would turn the matched column off by
 * the very name it was matched under.
 *
 * A column with no destination counterpart is still refused outright, naming it.
 * Loading a partial layout would hand back something that is not the View the
 * user asked for, and quietly.
 */
export function prepareTableTemplate(value: unknown, destination: TemplateDestination): {
  snapshot: TemplateSnapshot; reason?: string;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { snapshot: {}, reason: 'This View has no valid saved layout.' };
  }
  const snapshot = value as TemplateSnapshot;
  const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  const visibility = snapshot.columnVisibility ?? {};
  const visible = new Set<string>(strings(snapshot.fields ?? snapshot.columnOrder)
    .filter(id => visibility[id] !== false));
  for (const [id, shown] of Object.entries(visibility)) if (shown === true) visible.add(id);
  const required = new Set([
    ...visible,
    ...strings(snapshot.additionalFields).map(getApiFieldName).filter(id => visibility[id] !== false),
    ...strings(snapshot.grouping),
    ...strings(snapshot.pinnedColumns),
    ...Object.keys(snapshot.aggCalcs ?? {}).filter(id => snapshot.aggCalcs?.[id] != null),
    ...(Array.isArray(snapshot.sorting) ? snapshot.sorting.map((s: { id: string }) => s.id) : []),
    ...(Array.isArray(snapshot.columnFilters) ? snapshot.columnFilters.map((f: { id: string }) => f.id) : []),
  ]);
  // __select__ is generated by TanTable, rather than a data field.
  required.delete('__select__');
  const existing = new Set(destination.columnIds);
  const currentDynamic = new Set(destination.additionalFields.map(getApiFieldName));
  const addable = new Map<string, string>();
  for (const f of destination.availableFields ?? []) {
    for (const key of fieldnameKeys(f.fieldname)) if (!addable.has(key)) addable.set(key, f.fieldname);
  }
  // Destination columns by registry identity. Built in column order so that when
  // two columns claim one field the leftmost — the grid's own, ahead of any
  // appended dynamic extra — wins, the same "first one wins" the registry's own
  // name resolution uses.
  const byIdentity = new Map<string, string>();
  for (const id of destination.columnIds) {
    for (const key of canonicalFieldKeys(id, destination.fieldAliases)) {
      if (!byIdentity.has(key)) byIdentity.set(key, id);
    }
  }
  const sourceAliases = snapshot.fieldAliases as FieldAliasMap | undefined;
  /** The destination id for a source id, or undefined if the grid has no such
   *  column. An EXACT id match is always preferred: the overwhelming case is a
   *  View loading onto the grid it was saved on, and that must not depend on the
   *  registry knowing either grid. */
  const resolve = (id: string): string | undefined => {
    if (existing.has(id)) return id;
    for (const key of canonicalFieldKeys(id, sourceAliases)) {
      const hit = byIdentity.get(key);
      if (hit) return hit;
    }
    return undefined;
  };
  const additionalFields: string[] = [];
  const unavailable: string[] = [];
  /** Source id -> destination id, for every column the two grids spell differently. */
  const renames = new Map<string, string>();
  const link = (sourceId: string, destId: string) => {
    if (sourceId !== destId) renames.set(sourceId, destId);
  };
  for (const id of required) {
    const destId = resolve(id);
    if (destId) {
      link(id, destId);
      if (currentDynamic.has(destId)) additionalFields.push(getDbFieldName(destId));
      continue;
    }
    const fieldname = canonicalFieldKeys(id, sourceAliases)
      .map(key => addable.get(key))
      .find((name): name is string => !!name);
    if (destination.canAddFields && fieldname) {
      additionalFields.push(fieldname);
      // The column the host is about to build for it is id'd by the API name, so
      // a source id spelled otherwise still has to be renamed onto it.
      link(id, getApiFieldName(fieldname));
    } else {
      // Named as the SOURCE spelled it — that is what the user sees on the View
      // they are trying to load.
      unavailable.push(id);
    }
  }
  if (unavailable.length) {
    return { snapshot, reason: `Cannot load this View. These columns are unavailable on this table or you do not have access: ${unavailable.join(', ')}.` };
  }
  // Columns the View saved while HIDDEN are renamed too, even though none of them
  // could have blocked the load. They still carry an arrangement — where the column
  // sits, how wide it is, which filter type it uses — and that is exactly what the
  // user gets back the moment they unhide it. Leaving them under the source's
  // spelling would strand them: a dangling id is silently dropped on apply, which is
  // the very failure this resolution exists to end.
  for (const id of mentionedColumnIds(snapshot)) {
    if (renames.has(id)) continue;
    const destId = resolve(id);
    if (destId) link(id, destId);
  }
  const translated = renames.size ? renameSnapshotColumns(snapshot, renames, required) : snapshot;
  const translatedVisible = new Set([...visible].map(id => renames.get(id) ?? id));
  const columnVisibility = { ...(translated.columnVisibility ?? {}) };
  // Newer snapshots record the exact visible set. For older ones only hide
  // destination extras when the saved order provides a meaningful visible set.
  if (translatedVisible.size || Array.isArray(snapshot.fields)) {
    for (const id of destination.columnIds) if (!translatedVisible.has(id)) columnVisibility[id] = false;
    for (const id of translatedVisible) columnVisibility[id] = true;
  }
  // Deduped: two source columns resolving onto one destination dynamic column
  // would otherwise ask the host to fetch the same field twice.
  return { snapshot: { ...translated, additionalFields: [...new Set(additionalFields)], columnVisibility } };
}

/** Every column id a snapshot names anywhere, visible or not. */
function mentionedColumnIds(snapshot: TemplateSnapshot): Set<string> {
  const ids = new Set<string>();
  const add = (id: unknown) => { if (typeof id === 'string' && id !== '__select__') ids.add(id); };
  for (const key of ID_ARRAYS) {
    const list = snapshot[key];
    if (Array.isArray(list)) list.forEach(add);
  }
  for (const key of ID_RECORDS) {
    const record = snapshot[key];
    if (record && typeof record === 'object' && !Array.isArray(record)) Object.keys(record).forEach(add);
  }
  for (const key of ID_OBJECT_ARRAYS) {
    const list = snapshot[key];
    if (Array.isArray(list)) for (const entry of list) add((entry as { id?: unknown })?.id);
  }
  return ids;
}

/** Rewrite every id-keyed shape in a snapshot from the source grid's column ids to
 *  the destination's. Unlisted ids pass through untouched, so a snapshot key this
 *  does not know about is left exactly as saved rather than dropped.
 *
 *  Deliberately NOT rewritten: `additionalFields` (registry fieldnames, which
 *  prepareTableTemplate rebuilds from scratch anyway), `expanded` (keyed by ROW),
 *  and `selectedTabGroupField` — that one names a field in the ROW DATA, not a
 *  column, and the row key for a field differs per surface (the LC drill endpoints
 *  emit the raw fieldname where the bucket endpoints emit the API name). Renaming
 *  it as if it were a column id would point tab grouping at a key the rows do not
 *  have. handleLoadTemplate resets it to the host's own value for this reason. */
function renameSnapshotColumns(
  snapshot: TemplateSnapshot,
  renames: ReadonlyMap<string, string>,
  /** Source ids the layout actually depends on. Where two source columns collapse
   *  onto one destination column — a source grid carrying two spellings of one
   *  field — these win, so the surviving width/filter/sort is the one belonging to
   *  the column the View was actually built around. */
  preferred: ReadonlySet<string>,
): TemplateSnapshot {
  const to = (id: string) => renames.get(id) ?? id;
  const out: TemplateSnapshot = { ...snapshot };
  const write = (key: string, value: unknown) => { (out as Record<string, unknown>)[key] = value; };
  for (const key of ID_ARRAYS) {
    const list = snapshot[key];
    if (!Array.isArray(list)) continue;
    // Two source columns can land on one destination column (a grid holding both
    // spellings), so dedupe rather than emit it twice.
    const renamed = list.filter((id): id is string => typeof id === 'string').map(to);
    write(key, [...new Set(renamed)]);
  }
  for (const key of ID_RECORDS) {
    const record = snapshot[key];
    if (!record || typeof record !== 'object' || Array.isArray(record)) continue;
    const next: Record<string, unknown> = {};
    const entries = Object.entries(record);
    for (const [id, value] of entries) if (!preferred.has(id)) next[to(id)] = value;
    for (const [id, value] of entries) if (preferred.has(id)) next[to(id)] = value;
    write(key, next);
  }
  for (const key of ID_OBJECT_ARRAYS) {
    const list = snapshot[key];
    if (!Array.isArray(list)) continue;
    const seen = new Set<string>();
    write(key, list
      .map(entry => (
        entry && typeof entry === 'object' && typeof (entry as { id?: unknown }).id === 'string'
          ? { ...entry, id: to((entry as { id: string }).id) }
          : entry
      ))
      // One sort or filter per column: two source entries collapsing onto one
      // column would otherwise hand TanStack the same id twice.
      .filter(entry => {
        const id = (entry as { id?: unknown })?.id;
        if (typeof id !== 'string') return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }));
  }
  return out;
}
